import { useId } from 'react';
import {
  FormProvider,
  useFormContext,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Form plumbing. react-hook-form + Zod on every form, with schemas from `shared/` so
 * the client and the server validate against one definition.
 *
 * What this layer guarantees, so no screen has to remember it:
 *  - a real <label for> on every control — a placeholder is never a label
 *  - aria-invalid and aria-describedby wired to helper text and errors
 *  - a submit that is impossible to fire twice
 *  - on a failed submit, focus moves to the first invalid field
 */

interface FormProps<T extends FieldValues> extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  form: UseFormReturn<T>;
  onSubmit: SubmitHandler<T>;
}

export function Form<T extends FieldValues>({
  form,
  onSubmit,
  className,
  children,
  ...props
}: FormProps<T>) {
  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('space-y-4', className)}
        /*
         * Marked so the service-worker update prompt can see it.
         *
         * Reloading to apply a new version discards whatever is in an open form, and
         * `beforeunload` cannot help because the reload is ours — the browser does not
         * ask. One attribute here is cheaper than a global "is anything dirty"
         * register that every feature would have to remember to update.
         */
        data-dirty={form.formState.isDirty ? 'true' : undefined}
        {...props}
      >
        {/* The whole form is disabled while submitting, so a double submit cannot
            happen even if the button is somehow activated twice. */}
        <fieldset disabled={form.formState.isSubmitting} className="min-w-0 space-y-4 border-0 p-0">
          {children}
        </fieldset>
      </form>
    </FormProvider>
  );
}

/** Read a possibly dotted path (`bank.accountNumber`) out of the error tree. */
function errorAt(errors: Record<string, unknown>, name: string): { message?: string } | undefined {
  return name.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, errors) as { message?: string } | undefined;
}

export interface FieldRenderProps {
  id: string;
  'aria-invalid': boolean | undefined;
  'aria-describedby': string | undefined;
  'aria-required': boolean | undefined;
}

interface FormFieldProps {
  name: string;
  label: string;
  helper?: string;
  required?: boolean;
  optional?: boolean;
  className?: string;
  children: (field: FieldRenderProps) => React.ReactNode;
}

export function FormField({
  name,
  label,
  helper,
  required,
  optional,
  className,
  children,
}: FormFieldProps) {
  const uid = useId();
  const { formState } = useFormContext();

  const fieldId = `${uid}-field`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;

  const error = errorAt(formState.errors as Record<string, unknown>, name);
  const message = typeof error?.message === 'string' ? error.message : undefined;

  const describedBy =
    [helper ? helperId : null, message ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        htmlFor={fieldId}
        className="inline-flex items-center gap-1 text-label font-medium text-fg"
      >
        {label}
        {required && (
          <>
            <span aria-hidden className="text-danger">
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        )}
        {optional && <span className="font-normal text-fg-subtle">(optional)</span>}
      </label>

      {helper && (
        <p id={helperId} className="text-caption text-fg-muted">
          {helper}
        </p>
      )}

      {children({
        id: fieldId,
        'aria-invalid': message ? true : undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })}

      {message && (
        <p id={errorId} className="flex items-start gap-1.5 text-caption text-danger">
          <AlertCircle aria-hidden className="mt-px size-3.5 shrink-0" />
          {message}
        </p>
      )}
    </div>
  );
}

/**
 * Summary of every error, announced politely and shown at the head of the form.
 *
 * A long form whose only feedback is inline, below the fold, leaves the user pressing
 * Submit with nothing apparently happening.
 */
export function FormErrorSummary({ className }: { className?: string }) {
  const { formState } = useFormContext();
  const count = Object.keys(formState.errors).length;
  if (count === 0 || !formState.isSubmitted) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'flex items-start gap-2 rounded-md border border-danger-fill bg-danger-bg p-3',
        'text-caption text-danger',
        className,
      )}
    >
      <AlertCircle aria-hidden className="mt-px size-4 shrink-0" />
      <span>
        {count === 1
          ? 'There is 1 problem with this form. Please check the highlighted field.'
          : `There are ${count} problems with this form. Please check the highlighted fields.`}
      </span>
    </div>
  );
}

/** Footer actions. The primary action sits last so it is closest to the thumb. */
export function FormActions({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}
